import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { NuevaAxiosURL } from "../../../utils/utils";
import { getBackendError } from "../../../utils/error-helpers";
import MobileInput from "../../Mobile/Inputs/MobileInput";
import MobileButton from "../../Mobile/Buttons/MobileButton";
import ConfirmModal from "../../ui/Modal/ConfirmModal";
import BackButton from "../../ui/BackButton/BackButton";
import Checkbox from "../../ui/Checkbox/Checkbox";
import ElevvaLogo from "../../Mobile/Elements/ElevvaLogo";
import "./CreateAccount.css";
import "../Login/Login.css"; // Reuse Login styles for absolute consistency

const CreateAccount = ({ elevvaT = (key) => key }) => {
    const navigate = useNavigate();
    const [displayLoading, setDisplayLoading] = useState(false);
    const [inputs, setInputs] = useState({});
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: "",
        description: "",
        iconType: null,
        primaryAction: null,
        secondaryAction: null
    });

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    const handleChange = (event) => {
        const { name, value, type, files } = event.target;
        setInputs(prev => ({
            ...prev,
            [name]: type === "file" ? files[0] : value
        }));
    };

    const handlePhoneChange = (value) => {
        setInputs(prev => ({ ...prev, mobile_number: value }));
    };

    const handleCheckboxChange = (name, checked) => {
        setInputs(prev => ({ ...prev, [name]: checked }));
    };

    const autoLogin = async () => {
        const user = inputs.email?.trim();
        const password = inputs.password?.trim();

        if (!user || !password) return;

        const formData = new FormData();
        formData.append("email", user);
        formData.append("password", password);

        try {
            const loginResponse = await axios.post(`${NuevaAxiosURL}users/auth/login/`, formData);
            const data = loginResponse.data;

            if (data?.access) {
                localStorage.my_code = data.access;
                localStorage.refresh = data.refresh;
                localStorage.type = "Usuario";

                const instanceNew = axios.create({
                    baseURL: NuevaAxiosURL,
                    params: { t: new Date().getTime() },
                    headers: { Authorization: `Bearer ${data.access}` },
                });

                const companyData = await instanceNew.get(`memberships/corp/company`).then(res => res.data);
                localStorage.setItem("mob_api_key", companyData?.mob_api_key);
                localStorage.setItem("mob_integration", companyData?.mob_integration);
                localStorage.setItem("mob_currency", companyData?.currency);

                const userResponse = await instanceNew.get("users/");
                const userData = userResponse.data;

                localStorage.first_name = userData?.first_name;
                localStorage.last_name = userData?.last_name;
                localStorage.email = userData?.email;
                localStorage.id = userData?.id;
                localStorage.elevva_role = userData?.role;
                localStorage.privacy_policy_accepted = userData?.privacy_policy_accepted;
                localStorage.terms_and_conditions_accepted = userData?.terms_and_conditions_accepted;

                navigate("/auth/welcome");
            }
        } catch (err) {
            console.error("AutoLogin Error", err);
            navigate("/");
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const requiredFields = [
            "center_name", "center_nit", "email", "mobile_number",
            "password", "password2", "first_name", "last_name",
            "rut_file", "camara_comercio_file"
        ];

        const missing = requiredFields.filter(field => !inputs[field]);
        if (missing.length > 0) {
            setModalConfig({
                isOpen: true,
                title: "Campos incompletos",
                description: "Por favor completa todos los campos requeridos.",
                iconType: "info",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        if (inputs.password !== inputs.password2) {
            setModalConfig({
                isOpen: true,
                title: "Error",
                description: "Las contraseñas no coinciden.",
                iconType: "error",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        if (!inputs.terms_accepted || !inputs.privacy_policy_accepted || !inputs.advise_accepted) {
            setModalConfig({
                isOpen: true,
                title: "Acción requerida",
                description: "Debes aceptar los Términos, Políticas y la autorización de tratamiento de datos.",
                iconType: "info",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        setDisplayLoading(true);

        const formData = new FormData();
        formData.append("email", inputs.email);
        formData.append("password", inputs.password);
        formData.append("password2", inputs.password2);
        formData.append("first_name", inputs.first_name);
        formData.append("last_name", inputs.last_name);
        formData.append("mobile_number", inputs.mobile_number);
        formData.append("company_name", inputs.center_name);
        formData.append("nit", inputs.center_nit);
        formData.append("terms_accepted", "true");
        formData.append("terms_and_conditions_accepted", "true");
        formData.append("privacy_policy_accepted", "true");
        formData.append("advise_accepted", "true");
        formData.append("rut", inputs.rut_file);
        formData.append("commerce_document", inputs.camara_comercio_file);

        try {
            const res = await axios.post(`${NuevaAxiosURL}users/auth/corp/signup/`, formData);
            localStorage.company_id = res.data?.company_id;
            await autoLogin();
        } catch (err) {
            const msg = getBackendError(err);

            setModalConfig({
                isOpen: true,
                title: "Error",
                description: msg,
                iconType: "error",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
        } finally {
            setDisplayLoading(false);
        }
    };

    return (
        <div className="mobile-login-body-inner justify-content-start">
            <ConfirmModal {...modalConfig} onClose={closeModal} />

            <div className="d-flex flex-column gap-3 w-100">
                <div className="w-100 mb-4 animate-fade-in-up">
                    <div className="auth-header-flex">
                        <div className="auth-nav-area">
                            <BackButton />
                        </div>
                        <h1 className="auth-main-title">Crea tu cuenta</h1>
                        <div className="auth-nav-area hidden"></div>
                    </div>
                    <div className="recover-accent-line mx-auto"></div>
                </div>

                <form className="mobile-create-account-form animate-fade-in-up delay-1" onSubmit={handleSubmit}>
                    <MobileInput
                        type="text"
                        name="center_name"
                        label="Nombre del centro de entrenamiento"
                        placeholder="Ej: Elevva Gym"
                        required
                        onChange={handleChange}
                    />
                    <MobileInput
                        type="text"
                        name="center_nit"
                        label="NIT / Documento del centro"
                        placeholder="Ej: 900123456-7"
                        required
                        onChange={handleChange}
                    />
                    <div className="d-flex gap-2 mobile-input-row">
                        <MobileInput type="text" name="first_name" label="Nombre" placeholder="Tu nombre" required onChange={handleChange} />
                        <MobileInput type="text" name="last_name" label="Apellido" placeholder="Tu apellido" required onChange={handleChange} />
                    </div>
                    <MobileInput type="email" name="email" label="Correo electrónico" placeholder="ejemplo@email.com" required onChange={handleChange} />

                    <MobileInput
                        type="phone"
                        name="mobile_number"
                        label="Celular"
                        value={inputs.mobile_number}
                        required
                        onChange={handlePhoneChange}
                    />

                    <MobileInput type="password" name="password" label="Contraseña" placeholder="Mínimo 8 caracteres" required onChange={handleChange} />
                    <MobileInput type="password" name="password2" label="Confirmar contraseña" placeholder="Repite tu contraseña" required onChange={handleChange} />

                    <div className="d-flex gap-2 mobile-input-row">
                        <MobileInput
                            type="file"
                            name="rut_file"
                            label="RUT (PDF o Imagen)"
                            placeholder="Seleccionar archivo"
                            value={inputs.rut_file}
                            accept=".pdf,.png,.jpg"
                            onChange={handleChange}
                        />
                        <MobileInput
                            type="file"
                            name="camara_comercio_file"
                            label="Cámara de Comercio"
                            placeholder="Seleccionar archivo"
                            value={inputs.camara_comercio_file}
                            accept=".pdf,.png,.jpg"
                            onChange={handleChange}
                        />
                    </div>

                    <div className="mobile-terms-container">
                        <p className="mobile-terms-text">
                            Declaro que he leído, comprendido y acepto el contenido de la <a href="https://elevva.com.co/terminos-y-condiciones/" target="_blank" rel="noopener noreferrer">Política de Tratamiento de Datos Personales</a> y el <a href="https://elevva.com.co/aviso-de-privacidad/" target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>.
                        </p>

                        <div className="d-flex flex-column gap-3 mt-3">
                            <Checkbox
                                label={<>Acepto los <a href="https://elevva.com.co/terminos-y-condiciones/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Términos y Condiciones</a></>}
                                checked={inputs.terms_accepted || false}
                                onChange={(e) => handleCheckboxChange("terms_accepted", e.target.checked)}
                                fontSize="13px"
                                checkSize="22px"
                                align="center"
                            />

                            <Checkbox
                                label={<>Acepto la <a href="https://elevva.com.co/aviso-de-privacidad/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Política de Privacidad</a></>}
                                checked={inputs.privacy_policy_accepted || false}
                                onChange={(e) => handleCheckboxChange("privacy_policy_accepted", e.target.checked)}
                                fontSize="13px"
                                checkSize="22px"
                                align="center"
                            />

                            <Checkbox
                                label="He sido debidamente informado de que no estoy obligado(a) a consentir el tratamiento de mis datos sensibles y conociendo sus fines (detallados en la Política de Datos que acepto), autorizo libremente dicho tratamiento."
                                checked={inputs.advise_accepted || false}
                                onChange={(e) => handleCheckboxChange("advise_accepted", e.target.checked)}
                                fontSize="12px"
                                checkSize="22px"
                                align="flex-start"
                            />
                        </div>
                    </div>

                    <MobileButton
                        text="Registrarme"
                        type="submit"
                        className="mt-3"
                        loading={displayLoading}
                    />
                </form>
            </div>
        </div>
    );
};

export default React.memo(CreateAccount);
